/**
 * ReactorCore — cinema wave-1 item 2: the fleet reactor gauge (single SVG).
 *
 * Binds ONLY to real, already-fetched data (no new API fields, no fetches):
 *   mood     — derived by the mood engine (./fleetMood) in FleetHud.tsx
 *   overview — a small view-model derived in FleetHud.tsx from the EXISTING
 *              FleetOverview shape (latest_run.current_phase → stage,
 *              latest_run.items_summary → ticks, latest_run.created_at →
 *              elapsed_s / last_run_at).
 *
 * Motion contract (docs/plans/2026-08-12-hud-cinema-consult.md):
 * - All animation is CSS (transform / opacity / stroke only). The ONLY JS
 *   timer is the 1s useNow ticker driving the center elapsed readout — it
 *   interpolates now − realTimestamp, never synthesizes motion.
 * - Animation triggers are mood changes or real terminal statuses ONLY.
 * - No progress arcs: nothing here scales with elapsed_s / an estimate.
 * - prefers-reduced-motion collapses every animation to a static snapshot
 *   (pure CSS media query — no JS media query needed).
 * - Palette: near-black + #FF6600 + white at reduced opacity. No cyan/teal/ // design-law-allow
 *   navy/gold, no scanlines, no particles.
 */
import React, { useEffect, useRef } from 'react';
import { FleetMood, MOOD_STYLE, useNow } from './fleetMood';
import { parseTs } from './fleetTime';

/** Pipeline stages in order — the six arcs around the center disc. */
export const REACTOR_STAGES = ['PLAN', 'BUILD', 'GATE', 'MERGE', 'VERIFY', 'DEEP'] as const;

/** Item statuses that mean the item is done moving (one lit tick each).
    Exported for BridgeCore (wave-2 item 3) — the bridge-scale tick ring must
    light on exactly the same terminal set. */
export const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'merged',
  'verified',
  'failed',
  'halted',
  'dead_code',
  'resolved',
]);

export const ACCENT = '#FF6600'; // design-law-allow (canonical accent token definition)

/**
 * View-model the page derives from FleetOverview. `items_summary` may be
 * undefined when the API returns no items — the tick ring null-guards it.
 */
export interface ReactorCoreOverview {
  stage: string;
  items_summary?: Array<{ status?: string }>;
  elapsed_s: number;
  last_run_at: string | null;
}

interface ReactorCoreProps {
  mood: FleetMood;
  overview: ReactorCoreOverview;
  className?: string;
}

/** Map a 1-based current_phase onto the reactor stage order; unknown → ''. */
export function reactorStageForPhase(currentPhase?: number): string {
  if (!currentPhase || currentPhase < 1 || currentPhase > REACTOR_STAGES.length) return '';
  return REACTOR_STAGES[currentPhase - 1];
}

/**
 * Polar → cartesian around the LOCAL center (100, 100). Exported for
 * BridgeCore (wave-2 item 3), which renders at viewBox 480 — it wraps its
 * layers in a <g transform="translate(140 140)"> so this center-100 math is
 * reused verbatim rather than forked.
 */
export function polar(r: number, deg: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: 100 + r * Math.cos(rad), y: 100 + r * Math.sin(rad) };
}

/** Arc path for stage i of 6: a 50° sweep with 10° gaps, starting at top.
    Exported for BridgeCore — same local-center caveat as polar(). */
export function arcPath(r: number, index: number, count: number): string {
  const slot = 360 / count;
  const a0 = index * slot + 5;
  const a1 = (index + 1) * slot - 5;
  const p0 = polar(r, a0);
  const p1 = polar(r, a1);
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
}

/** 'Xm Ys' — exactly the readout format the consult spec fixes. Exported for
    BridgeCore's elapsed line (same format at bridge scale). */
export function formatMinSec(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/**
 * Component-scoped CSS. Durations that must track the mood period are set
 * inline from MOOD_STYLE[mood].pulseSeconds (see the disc/ring styles) so a
 * mood change re-binds speed immediately; everything else lives in these
 * classes — one disc class per mood variant, play-state via class only.
 */
const RC_CSS = `
.rc-disc { transform-box: fill-box; transform-origin: center; }
.rc-disc--idle,
.rc-disc--waiting,
.rc-disc--building { animation-name: rc-pulse; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
.rc-disc--healing { animation-name: rc-pulse-heal; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
.rc-disc--halted { animation-name: rc-flicker; animation-timing-function: steps(2, end); animation-iteration-count: infinite; }
.rc-disc--down { animation: none; }
.rc-arc-current { animation: rc-march 1.2s linear infinite; }
.rc-outer-ring { animation-name: rc-spin; animation-timing-function: linear; animation-iteration-count: infinite; transform-box: fill-box; transform-origin: center; }
.rc-outer-ring--paused { animation-play-state: paused; }
@keyframes rc-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.72; transform: scale(1.045); } }
@keyframes rc-pulse-heal { 0%, 100% { opacity: 1; stroke: ${ACCENT}; } 50% { opacity: 0.7; stroke: #FF9933; } } /* design-law-allow (healing pulse secondary) */
@keyframes rc-flicker { 0% { opacity: 1; stroke: ${ACCENT}; } 50% { opacity: 0.5; stroke: #FF3300; } } /* design-law-allow (flicker warning color) */
@keyframes rc-march { to { stroke-dashoffset: -24; } }
@keyframes rc-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .rc-root, .rc-root * { animation: none !important; }
}
`;

export default function ReactorCore({ mood, overview, className }: ReactorCoreProps) {
  const now = useNow(1000);

  // Elapsed ticker: anchor on the poll-time elapsed_s, interpolate forward
  // with the 1s useNow tick; re-anchor whenever a new poll value arrives.
  const anchorRef = useRef({ base: overview.elapsed_s, at: Date.now() });
  useEffect(() => {
    anchorRef.current = { base: overview.elapsed_s, at: Date.now() };
  }, [overview.elapsed_s]);
  const elapsedS = Math.max(
    0,
    Math.floor(anchorRef.current.base + (now - anchorRef.current.at) / 1000)
  );

  const pulseSeconds = MOOD_STYLE[mood].pulseSeconds;
  const glow = MOOD_STYLE[mood].glow;
  const isDown = mood === 'down';
  const isIdle = mood === 'idle';

  // Pipeline arcs: done / current (case-insensitive match) / upcoming.
  // Unknown stage → index -1 → every arc upcoming.
  const stageIdx = REACTOR_STAGES.findIndex(
    (s) => s === (overview.stage ?? '').toUpperCase()
  );

  // Item ticks — null-guarded: the API may omit items_summary entirely.
  const items = overview.items_summary ?? [];

  // Center readout lines per mood (exact strings fixed by the spec).
  let line1: string;
  let line2: string | null;
  if (isDown) {
    line1 = 'OFFLINE';
    line2 = null;
  } else if (isIdle) {
    line1 = 'STANDBY';
    if (overview.last_run_at === null) {
      line2 = 'never';
    } else {
      const ms = parseTs(overview.last_run_at);
      if (ms === null) {
        line2 = 'never';
      } else {
        const ago = Math.max(0, Math.floor((now - ms) / 1000));
        line2 = `last run ${Math.floor(ago / 3600)}h ${Math.floor((ago % 3600) / 60)}m ago`;
      }
    }
  } else {
    line1 = (overview.stage ?? '').toUpperCase() || '—';
    line2 = formatMinSec(elapsedS);
  }

  const ringPaused = mood === 'halted' || isDown;

  return (
    <div className={`rc-root flex justify-center ${className ?? ''}`}>
      {/* Mobile-first: 96px compact by default, 220px from sm up. The fixed
          viewBox scales text with the SVG, so the compact variant can never
          clip the center readout. */}
      <svg
        viewBox="0 0 200 200"
        className="h-24 w-auto sm:h-[220px]"
        role="img"
        aria-label={`fleet reactor core — ${MOOD_STYLE[mood].label}`}
        data-testid="reactor-core"
      >
        <style>{RC_CSS}</style>

        {/* Outer dashed ring — continuous spin at mood-period speed; paused
            (via class, never JS state) while halted or down. */}
        <circle
          cx="100"
          cy="100"
          r="94"
          fill="none"
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="1"
          strokeDasharray="3 7"
          className={`rc-outer-ring${ringPaused ? ' rc-outer-ring--paused' : ''}`}
          style={{ animationDuration: `${pulseSeconds > 0 ? pulseSeconds : 4}s` }}
          data-testid="rc-outer-ring"
        />

        {/* Item-tick ring — one tick per real items_summary entry; lit when
            the entry's status is terminal. */}
        {items.map((entry, i) => {
          const deg = (i * 360) / items.length;
          const p0 = polar(78, deg);
          const p1 = polar(86, deg);
          const lit = !!entry.status && TERMINAL_STATUSES.has(entry.status);
          return (
            <line
              key={`tick-${i}`}
              x1={p0.x.toFixed(2)}
              y1={p0.y.toFixed(2)}
              x2={p1.x.toFixed(2)}
              y2={p1.y.toFixed(2)}
              stroke={lit ? ACCENT : 'rgba(255,255,255,0.2)'}
              strokeOpacity={lit ? 1 : 0.2}
              strokeWidth="2"
              strokeLinecap="round"
              data-testid={`rc-tick-${i}`}
              data-lit={lit ? 'true' : 'false'}
            />
          );
        })}

        {/* Pipeline arc ring — six stages, evenly spaced. */}
        {REACTOR_STAGES.map((stage, i) => {
          const state = stageIdx === -1 || i > stageIdx ? 'upcoming' : i === stageIdx ? 'current' : 'done';
          return (
            <path
              key={stage}
              d={arcPath(66, i, REACTOR_STAGES.length)}
              fill="none"
              stroke={state === 'upcoming' ? '#FFFFFF' : ACCENT} // design-law-allow (white for upcoming state)
              strokeOpacity={state === 'upcoming' ? 0.1 : 1}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={state === 'current' ? '7 5' : state === 'upcoming' ? '2 6' : undefined}
              className={state === 'current' ? 'rc-arc-current' : undefined}
              data-testid={`rc-arc-${stage}`}
              data-state={state}
            />
          );
        })}

        {/* Center disc — fill/glow bound to the mood; one animation class per
            mood variant; down = gray, animation:none. */}
        <circle
          cx="100"
          cy="100"
          r="34"
          fill={isDown ? 'rgba(255,255,255,0.10)' : glow}
          stroke={isDown ? 'rgba(255,255,255,0.30)' : ACCENT}
          strokeWidth="2"
          className={`rc-disc rc-disc--${mood}`}
          style={{
            animationDuration: `${pulseSeconds}s`,
            filter: isDown ? undefined : `drop-shadow(0 0 8px ${glow})`,
          }}
          data-testid="rc-disc"
        />

        {/* Center readout — DM Mono via the house font-mono token. */}
        <text
          x="100"
          y={line2 === null ? 104 : 96}
          textAnchor="middle"
          className="font-mono"
          fontSize="13"
          fill="rgba(255,255,255,0.92)"
          data-testid="rc-readout-line1"
        >
          {line1}
        </text>
        {line2 !== null && (
          <text
            x="100"
            y="112"
            textAnchor="middle"
            className="font-mono"
            fontSize={line2.length > 10 ? 8 : 9}
            fill="rgba(255,255,255,0.55)"
            data-testid="rc-readout-line2"
          >
            {line2}
          </text>
        )}
      </svg>
    </div>
  );
}
